import { Injectable, inject } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular/standalone';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { EventName } from 'src/app/core/services/analytics/event-names';
import {
  getModalConfig,
  modalContent,
  shouldShowAsSheetModal,
} from 'src/app/core/services/modal/modal.utils';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { SoloTiersService } from 'src/app/core/services/tiers/solo-tiers.service';
import { SoloQuoteService } from 'src/app/core/services/tiers/solo-quote.service';
import { GameId } from 'src/app/shared/models/game/game-id';
import { PaymentResult, PaymentService } from 'src/app/features/payments/services/payment.service';
import { SoloSheetPayment } from 'src/app/features/payments/services/solo-sheet-payment';
import { SoloSheetComponent } from '../solo-sheet/solo-sheet.component';

/**
 * Opens the Solo sheet from either tier surface (the wager drawer's controls and the tier-first
 * controls). PaymentService.openSoloSheet owns the payment latch and builds the payment the sheet
 * pays with, then hands it back here to present, so a sheet only ever opens inside the latch.
 */
@Injectable({
  providedIn: 'root',
})
export class SoloSheetService {
  private modalController = inject(ModalController);
  private platform = inject(Platform);
  private themeService = inject(ThemeService);
  private soloTiersService = inject(SoloTiersService);
  private soloQuoteService = inject(SoloQuoteService);
  private analyticsService = inject(AnalyticsService);
  private paymentService = inject(PaymentService);

  // Resolves once the sheet has closed and any charge it started has settled; dropped when
  // another payment already holds the latch. With no stake listed, the tiers are refetched first
  // and the sheet is skipped if that finds none either: Play again on the result drawer opens it
  // without a card to gate it, and a sheet presented empty would only close itself at once.
  async open(gameId: GameId): Promise<PaymentResult> {
    const refreshed = this.soloTiersService.soloTiers().length === 0;
    if (refreshed) {
      await this.soloTiersService.refresh();
      if (this.soloTiersService.soloTiers().length === 0) {
        return { dropped: false };
      }
    }
    return this.paymentService.openSoloSheet(gameId, (payment, onPresented) =>
      this.show(gameId, payment, onPresented, !refreshed),
    );
  }

  // Resolves once the sheet has dismissed. `onPresented` gets the sheet as soon as it is up, so
  // the payment has a sheet to close before it starts anything that can fail.
  private async show(
    gameId: GameId,
    payment: SoloSheetPayment,
    onPresented: (sheet: HTMLIonModalElement) => void,
    refresh: boolean,
  ): Promise<void> {
    // Fetched as the sheet is built rather than once it is up, so the quote's target and countdown
    // usually replace the tiers' target before the player has read it. Kept live while the sheet
    // is open.
    this.soloQuoteService.start(gameId);
    try {
      await this.present(payment, onPresented, refresh);
    } finally {
      this.soloQuoteService.stop();
    }
  }

  private async present(
    payment: SoloSheetPayment,
    onPresented: (sheet: HTMLIonModalElement) => void,
    refresh: boolean,
  ): Promise<void> {
    const asSheetModal = shouldShowAsSheetModal(this.platform, this.themeService);
    const modal = await this.modalController.create({
      ...modalContent(SoloSheetComponent, { payment }),
      ...getModalConfig(asSheetModal),
    });
    await modal.present();
    // Logged here rather than on the card tap so every way in counts once: the wager drawer, the
    // tier-first list and the Solo result drawer's Play again.
    this.analyticsService.log(EventName.SoloSheetViewed);
    onPresented(modal);

    // The target moves, so the sheet shows the latest one rather than the card's. The
    // sheet reads the tiers live and closes itself if this finds none left. Skipped when `open`
    // has just fetched them.
    if (refresh) {
      void this.soloTiersService.refresh();
    }

    await modal.onDidDismiss();
  }
}
